// Modern Marine / BoatWise Theme
export const theme = {
  colors: {
    // Primary brand colors
    primary: {
      main: '#1e3a8a', // Deep marine blue
      light: '#3b82f6', // Bright blue
      dark: '#1e40af', // Darker blue
      contrast: '#ffffff'
    },
    
    // Secondary colors
    secondary: {
      main: '#0ea5e9', // Ocean blue
      light: '#38bdf8', // Light ocean
      dark: '#0284c7', // Dark ocean
      contrast: '#ffffff'
    },
    
    // Accent colors
    accent: {
      main: '#f59e0b', // Marine gold
      light: '#fbbf24', // Light gold
      dark: '#d97706', // Dark gold
      contrast: '#1f2937'
    },
    
    // Success colors
    success: {
      main: '#10b981', // Marine green
      light: '#34d399', // Light green
      dark: '#059669', // Dark green
      contrast: '#ffffff'
    },
    
    // Warning colors
    warning: {
      main: '#f59e0b', // Marine gold
      light: '#fbbf24', // Light gold
      dark: '#d97706', // Dark gold
      contrast: '#1f2937'
    },
    
    // Error colors
    error: {
      main: '#ef4444', // Marine red
      light: '#f87171', // Light red
      dark: '#dc2626', // Dark red
      contrast: '#ffffff'
    },
    
    // Neutral colors
    neutral: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a'
    },
    
    // Background colors
    background: {
      primary: '#0f172a', // Deep marine dark
      secondary: '#1e293b', // Marine dark
      tertiary: '#334155', // Marine medium
      paper: '#1e293b',
      card: '#334155'
    },
    
    // Text colors
    text: {
      primary: '#f8fafc',
      secondary: '#cbd5e1',
      disabled: '#64748b',
      inverse: '#0f172a'
    },
    
    // Border colors
    border: {
      primary: '#475569',
      secondary: '#334155',
      accent: '#3b82f6'
    },
    
    // Input colors
    input: {
      background: '#1e293b',
      text: '#f8fafc',
      placeholder: '#94a3b8',
      border: '#475569',
      focus: '#3b82f6'
    },
    
    // Button colors
    button: {
      disabledBackground: '#475569',
      disabledText: '#94a3b8'
    },
    
    // Info colors
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
      contrast: '#ffffff'
    },
    
    // Status colors for widgets
    status: {
      healthy: '#10b981',    // Green - Good status
      warning: '#f59e0b',    // Yellow - Warning
      critical: '#ef4444',   // Red - Critical
      offline: '#6b7280'     // Gray - Offline
    },
    
    // Marine specific colors
    marine: {
      water: '#0ea5e9',
      sky: '#38bdf8',
      sand: '#fbbf24',
      coral: '#f87171',
      seaweed: '#10b981',
      sunset: '#f59e0b'
    }
  },
  
  // Typography
  typography: {
    fontFamily: {
      primary: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
      secondary: '"Poppins", "Inter", sans-serif',
      mono: '"JetBrains Mono", "Fira Code", "Monaco", monospace'
    },
    
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
      '5xl': '3rem',
      '6xl': '3.75rem'
    },
    
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800
    },
    
    lineHeight: {
      none: 1,
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2
    }
  },
  
  // Compatibility aliases for widgets
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    h1: '3rem',
    h2: '2.25rem',
    h3: '1.875rem',
    h4: '1.5rem'
  },
  
  fontWeights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800
  },
  
  // Spacing
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
    '4xl': '6rem'
  },
  
  // Border radius
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    base: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px'
  },
  
  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    none: 'none'
  },
  
  // Transitions
  transitions: {
    default: 'all 0.2s ease-in-out',
    fast: 'all 0.1s ease-in-out',
    slow: 'all 0.3s ease-in-out',
    transform: 'transform 0.2s ease-in-out',
    opacity: 'opacity 0.2s ease-in-out',
    colors: 'color 0.2s ease-in-out, background-color 0.2s ease-in-out, border-color 0.2s ease-in-out'
  },
  
  // Breakpoints - mobileLg targets iPhone 13 Pro Max (428px viewport)
  breakpoints: {
    xs: '480px',
    mobileLg: '428px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px'
  },

  // iPhone 13 Pro Max viewport (CSS pixels) - 6.7" display, 428×926, DPR 3
  viewports: {
    iphone13ProMax: { width: 428, height: 926 }
  },
  
  // Z-index
  zIndex: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800
  }
};

// Export individual color palettes for easy access
export const colors = theme.colors;
export const typography = theme.typography;
export const spacing = theme.spacing;
export const borderRadius = theme.borderRadius;
export const shadows = theme.shadows;
export const transitions = theme.transitions;
export const breakpoints = theme.breakpoints;
export const zIndex = theme.zIndex;

export default theme;
